import React, {useCallback, useEffect, useState, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {
  FaExclamationCircle,
  FaFileAlt,
  FaCheckCircle,
  FaPaperclip,
  FaCloudUploadAlt,
  FaExclamationTriangle,
  FaEye,
} from 'react-icons/fa'
import axios from 'axios'
import './../style.css'
import ChatModal from './ChatModal'
import DocumentPreviewModal from './DocumentPreviewModal'
import {toast} from 'react-toastify'
import {useAuth} from '../../../modules/auth'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {createPusherClient} from '../../../../_metronic/helpers/pusherClient'

interface DocumentsProps {
  claimDetails: any
  onDocumentClick: (docLabel: string) => void
  onDocumentsStatusChange: (status: boolean) => void
  onDocumentValidationChange?: (hasInvalid: boolean) => void
  onDocumentRealtimeUpdate?: (data: any) => void
  setIsReupload: (isReupload: boolean) => void
}

interface ExpectedDocument {
  label: string
  key: number
}

const DocumentsFGL: React.FC<DocumentsProps> = ({
  claimDetails,
  onDocumentsStatusChange,
  onDocumentValidationChange,
  onDocumentRealtimeUpdate,
  setIsReupload,
}) => {
  const user = useAuth()
  const {auth} = useAuth()
  const {formatMessage: f} = useIntl()
  const [documentFiles, setDocumentFiles] = useState<Record<string, any[]>>({})
  const [documentsData, setDocumentsData] = useState<any[]>([])
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false)
  const [chatMessage, setChatMessage] = useState<string>('')
  const [isLoadingDocuments, setIsLoadingDocuments] = useState<boolean>(false)
  const [recentlyUploadedLabels, setRecentlyUploadedLabels] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem('recentlyUploadedLabels')
      return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch {
      return new Set()
    }
  })

  const [previewData, setPreviewData] = useState<{
    isOpen: boolean
    fileName: string
    label: string
  }>({
    isOpen: false,
    fileName: '',
    label: '',
  })

  const expectedDocuments: ExpectedDocument[] = useMemo(
    () => [
      {label: f({id: 'CLAIM.INPATIENT.RESUME_MEDICAL'}), key: 0},
      {label: f({id: 'CLAIM.INPATIENT.BILLING_FINAL'}), key: 1},
      {label: f({id: 'CLAIM.INPATIENT.LAB_SUPPORTING_RESULT'}), key: 2},
    ],
    [f]
  )

  const documentLabelMapping = {
    'Discharge Summary': 'Resume Medis',
    'Final Bill': 'Billing Akhir',
    'Supporting Document': 'Hasil Penunjang',
    'Resume Medis': 'Discharge Summary',
    'Billing Akhir': 'Final Bill',
    'Hasil Penunjang': 'Supporting Document',
  }

  const normalizeDocumentLabel = (label: string): string => {
    const currentLabels = expectedDocuments.map((doc) => doc.label)
    if (currentLabels.includes(label)) return label
    if (documentLabelMapping[label]) return documentLabelMapping[label]
    return label
  }

  /**
   * ListFiles can take many seconds (IDH-1619 — svc.claim awaits a Pusher publish
   * inside the GET). Until it returns, the rows below would otherwise read as
   * "no file uploaded" with a live Upload button, which invites a duplicate
   * upload. The Pusher handler passes `background: true` so a real-time refresh
   * does not flash the indicator on every file event.
   */
  const fetchDocuments = useCallback(
    async ({background = false}: {background?: boolean} = {}) => {
      const claimNumber = claimDetails.claimDetails.claim.claimNumber
      const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}?area=Discharge`

      if (!background) setIsLoadingDocuments(true)

      try {
        const response = await axios.get(apiUrl, {
          headers: {Authorization: `Bearer ${auth?.token || user.currentUser?.token}`},
        })
        if (response.data.isSuccess) {
          const apiDocuments = response.data.data.map((doc: any) => ({
            ...doc,
            label: normalizeDocumentLabel(doc.label),
          }))

          const allDocsMap = new Map()
          expectedDocuments.forEach((doc) => allDocsMap.set(doc.label, {...doc, document: []}))
          apiDocuments.forEach((doc: any) => {
            if (allDocsMap.has(doc.label)) {
              allDocsMap.get(doc.label).document = doc.document
            } else {
              allDocsMap.set(doc.label, doc)
            }
          })

          const finalDocs = Array.from(allDocsMap.values())
          setDocumentsData(finalDocs)

          const updatedFiles: Record<string, any[]> = {}
          finalDocs.forEach((doc) => {
            updatedFiles[doc.label] = doc.document || []
          })
          setDocumentFiles(updatedFiles)

          const hasInvalid = apiDocuments.some((doc: any) =>
            doc.document?.some((file: any) => file.status === 'INVALID')
          )
          const allUploaded = expectedDocuments.every((doc) => updatedFiles[doc.label]?.length > 0)

          onDocumentsStatusChange(allUploaded && !hasInvalid)
          if (onDocumentValidationChange) onDocumentValidationChange(hasInvalid)
        }
      } catch (error) {
        console.error('Error fetching FGL documents:', error)
      } finally {
        if (!background) setIsLoadingDocuments(false)
      }
    },
    [
      claimDetails?.claimDetails?.claim?.claimNumber,
      auth?.token,
      user.currentUser?.token,
      expectedDocuments,
    ]
  )

  useEffect(() => {
    fetchDocuments()
  }, [claimDetails?.claimDetails?.claim?.claimNumber, fetchDocuments])

  useEffect(() => {
    const pusher = createPusherClient('InpatientDocsFGL')
    const claimNumber = claimDetails.claimDetails.claim.claimNumber
    const listFileChannel = pusher.subscribe(`list-file-channel-${claimNumber}`)

    listFileChannel.bind(`get-list-${claimNumber}`, (data: any) => {
      if (data.isSuccess) {
        if (onDocumentRealtimeUpdate) onDocumentRealtimeUpdate(data)
        // Background: a real-time refresh must not flash the loading indicator.
        fetchDocuments({background: true})
      }
    })

    return () => {
      listFileChannel.unbind_all()
      pusher.unsubscribe(`list-file-channel-${claimNumber}`)
      pusher.disconnect()
    }
  }, [claimDetails.claimDetails.claim.claimNumber, fetchDocuments])

  const isInteractionDisabled = () => {
    const disabledStatuses = [
      'Admission Review',
      'Request IGL',
      'IGL RELEASED',
      'Daily Monitoring',
      'Request FGL',
      'FGL RELEASED',
      'Reject',
      'Void',
      'IGL Released',
      'FGL Released',
      'FGL Review',
    ]
    // Also disabled while ListFiles is in flight — the current file state is
    // unknown until it returns, so uploading now risks a duplicate.
    return (
      isLoadingDocuments || disabledStatuses.includes(claimDetails?.claimDetails?.claim?.status)
    )
  }

  const handleFileChange = async (label: string, event: React.ChangeEvent<HTMLInputElement>) => {
    if (isInteractionDisabled()) return
    if (event.target.files && event.target.files.length > 0) {
      const selectedFiles = Array.from(event.target.files)
      const MAX_FILE_SIZE = 10 * 1024 * 1024

      if (selectedFiles.some((file) => file.size > MAX_FILE_SIZE)) {
        toast.error('File size exceeds 10MB limit')
        return
      }

      try {
        const fileInput = event.target as HTMLInputElement
        const currentDoc = documentsData.find((doc) => doc.label === label)
        const currentFileId = fileInput.dataset.replaceFileId
          ? parseInt(fileInput.dataset.replaceFileId)
          : currentDoc?.document?.[0]?.id

        setRecentlyUploadedLabels((prev) => {
          const next = new Set(prev).add(label)
          sessionStorage.setItem('recentlyUploadedLabels', JSON.stringify(Array.from(next)))
          return next
        })
        setDocumentFiles((prev) => ({
          ...prev,
          [label]: [{name: selectedFiles[0].name, status: 'IDLE', id: currentFileId || 0}],
        }))

        await uploadFiles(label, selectedFiles, currentFileId)
        if (fileInput.dataset.replaceFileId) delete fileInput.dataset.replaceFileId
        setIsReupload(true)
      } catch (error) {
        console.error('FGL Upload error:', error)
      }
    }
  }

  const uploadFiles = async (label: string, files: File[], existingFileId?: number) => {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))

    const claimNumber = claimDetails.claimDetails.claim.claimNumber
    const documentId = existingFileId || 0
    const apiUrl = `${
      process.env.REACT_APP_API_CLAIM_URL
    }/api/Claims/UploadFiles?claimNumber=${claimNumber}&typeDocument=${encodeURIComponent(
      label
    )}&user=${user.currentUser?.email || 'SYSTEM'}&bucket=Discharge&id=${documentId}`

    try {
      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${user.currentUser?.token}`,
        },
      })
      if (response.data.isSuccess) {
        toast.success('File uploaded successfully')
        // Background: a post-upload refresh must not blank the grid either.
        fetchDocuments({background: true})
      } else {
        throw new Error(response.data.message)
      }
    } catch (error) {
      toast.error('Upload failed')
    }
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'VALID':
        return 'valid'
      case 'INVALID':
      case 'REQUEST':
      case 'REQUEST DOCUMENT':
        return 'request'
      case 'IDLE':
        return 'idle'
      default:
        return ''
    }
  }

  const getDocumentStatusText = (file: any) => {
    if (!file?.name) return 'Not uploaded'
    if (file.status === 'VALID') return 'Approved'
    if (file.status === 'INVALID') return 'Revision needed'
    return 'Uploaded'
  }

  return (
    <>
      <ToastContainer position='top-right' autoClose={3000} theme='light' style={{zIndex: 9999}} />
      <div className='documents-container'>
        {isLoadingDocuments && (
          <div
            className='d-flex align-items-center justify-content-center gap-2 py-4'
            style={{gridColumn: '1 / -1'}}
            role='status'
            aria-live='polite'
          >
            <div className='spinner-border spinner-border-sm text-primary' />
            <span className='text-primary fw-bold' style={{fontSize: '12px'}}>
              Loading documents...
            </span>
          </div>
        )}
        {documentsData
          .filter((doc) => doc.label !== 'Chat Document')
          .map((doc) => {
            const files = documentFiles[doc.label] || []
            const isInvalid = files.some((f) => f.status === 'INVALID')
            const isValid = files.some((f) => f.status === 'VALID')
            const isIdle = files.some((f) => f.status === 'IDLE')
            const isRequest = files.some(
              (f) => f.status === 'REQUEST' || f.status === 'Request Document'
            )
            const isRecentlyUploaded = recentlyUploadedLabels.has(doc.label)

            const showReplace =
              (isInvalid || (isIdle && isRecentlyUploaded)) && !isInteractionDisabled()
            const showUpload = (isRequest || files.length === 0) && !isInteractionDisabled()

            return (
              <div key={doc.label} className='document-item mb-4'>
                <div className='document-header'>
                  <div className='document-header-content d-flex align-items-center gap-3'>
                    <div
                      className={`document-icon ${
                        isValid ? 'success' : isInvalid || isRequest ? 'request' : 'warning'
                      }`}
                    >
                      {isValid ? (
                        <FaCheckCircle />
                      ) : isInvalid || isRequest ? (
                        <FaExclamationTriangle />
                      ) : (
                        <FaExclamationCircle />
                      )}
                    </div>

                    <div className='flex-grow-1'>
                      <span className='document-text fw-bold d-block'>{doc.label}</span>
                      <div className='d-flex gap-2 mt-1'>
                        {isRecentlyUploaded && (
                          <span className='badge-pill recent'>Recently uploaded</span>
                        )}
                        {isInvalid && <span className='badge-pill invalid'>Invalid Document</span>}
                      </div>
                    </div>

                    <div className='action-matrix d-flex gap-2'>
                      {files.length > 0 && files[0].name && (
                        <button
                          className='btn-preview'
                          onClick={() =>
                            setPreviewData({
                              isOpen: true,
                              fileName: files[0].name,
                              label: doc.label,
                            })
                          }
                        >
                          <FaEye />
                        </button>
                      )}
                      {showReplace && (
                        <button
                          className='btn-replace'
                          onClick={() => {
                            const input = document.getElementById(
                              `upload-${doc.label}`
                            ) as HTMLInputElement
                            if (input) {
                              input.dataset.replaceFileId = files[0]?.id?.toString()
                              input.click()
                            }
                          }}
                        >
                          Replace
                        </button>
                      )}
                      {showUpload && (
                        <button
                          className='btn-upload'
                          onClick={() => {
                            const input = document.getElementById(
                              `upload-${doc.label}`
                            ) as HTMLInputElement
                            if (input) {
                              const invalidFile = files.find(
                                (f) => f.status === 'INVALID' || f.status === 'Request Document'
                              )
                              if (invalidFile) {
                                input.dataset.replaceFileId = invalidFile.id.toString()
                              }
                              input.click()
                            }
                          }}
                        >
                          Upload Document
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {files.length > 0 && files[0].name && (
                  <div className='file-details border-top p-3 bg-light d-flex align-items-center gap-2'>
                    <div className='file-pill flex-grow-1 text-truncate' title={files[0].name}>
                      <FaPaperclip className='me-2 text-primary' />
                      {files[0].name}
                    </div>
                    <span className={`status-badge ${getStatusBadgeClass(files[0].status)}`}>
                      {getDocumentStatusText(files[0])}
                    </span>
                  </div>
                )}

                <input
                  id={`upload-${doc.label}`}
                  type='file'
                  accept='.pdf,.jpg,.jpeg,.png'
                  style={{display: 'none'}}
                  onChange={(e) => handleFileChange(doc.label, e)}
                />
              </div>
            )
          })}
      </div>

      <style>{`
        .documents-container {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin: 0 auto;
          padding: 12px;
        }

        .document-item {
          background: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          overflow: hidden;
          border: 1px solid #e9ecef;
          display: flex;
          flex-direction: column;
          height: 100%;
          transition: all 0.2s ease;
        }

        .document-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }

        @media (max-width: 992px) {
          .documents-container {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 576px) {
          .documents-container {
            grid-template-columns: 1fr;
          }
        }

        .document-header {
          padding: 10px 12px;
          background: #ffffff;
          border-bottom: 1px solid #f1f3f5;
          cursor: pointer;
        }

        .document-header-content {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 4px;
        }

        .document-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          flex-shrink: 0;
        }
        .document-icon.success { background: rgba(32,201,151,0.1); color: #20C997; }
        .document-icon.request { background: rgba(250,82,82,0.1); color: #FA5252; }
        .document-icon.warning { background: rgba(253,126,20,0.1); color: #FD7E14; }

        .document-text {
          font-weight: 500;
          color: #2c3e50;
          font-size: 13px;
          flex: 1;
        }

        .badge-pill {
          padding: 2px 8px;
          border-radius: 20px;
          font-size: 10px;
          font-weight: 600;
          border: 1px solid transparent;
          white-space: nowrap;
        }
        .badge-pill.recent { background: rgba(5,155,182,0.1); color: #059BB6; border-color: rgba(5,155,182,0.2); }
        .badge-pill.invalid { background: rgba(250,82,82,0.1); color: #FA5252; border-color: rgba(250,82,82,0.2); }

        .action-matrix {
          display: flex;
          gap: 6px;
          align-items: center;
          flex-shrink: 0;
        }

        .btn-preview {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: none;
          background: #f8f9fa;
          color: #059BB6;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          font-size: 13px;
        }
        .btn-preview:hover { background: #e2e8f0; color: #047d92; }

        .btn-replace {
          padding: 5px 14px;
          border-radius: 6px;
          border: 1px solid #059BB6;
          background: transparent;
          color: #059BB6;
          font-size: 11px;
          font-weight: 700;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .btn-replace:hover { background: rgba(5,155,182,0.05); }

        .btn-upload {
          padding: 5px 14px;
          border-radius: 6px;
          border: none;
          background: #059BB6;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          box-shadow: 0 4px 6px -1px rgba(5,155,182,0.3);
          transition: all 0.2s;
          white-space: nowrap;
        }
        .btn-upload:hover { background: #047d92; transform: translateY(-1px); }

        .upload-progress {
          padding: 8px 12px;
          border-top: 1px solid #f1f3f5;
        }

        .file-details {
          border-top: 1px solid #f1f3f5;
          padding: 10px 12px;
          background: #fafbfc;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .file-pill {
          background: #fff;
          border: 1px solid #e2e8f0;
          padding: 5px 10px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          color: #4a5568;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .status-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 700;
          background: #f7fafc;
          min-width: 80px;
          text-align: center;
          flex-shrink: 0;
        }
        .status-badge.valid { background: rgba(32,201,151,0.1); color: #20C997; }
        .status-badge.request { background: rgba(250,82,82,0.1); color: #FA5252; }
        .status-badge.idle { background: rgba(253,126,20,0.1); color: #FD7E14; }
      `}</style>

      <ChatModal
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        message={chatMessage}
        claimDetails={claimDetails}
      />
      <DocumentPreviewModal
        isOpen={previewData.isOpen}
        claimNumber={claimDetails?.claimDetails?.claim?.claimNumber}
        fileName={previewData.fileName}
        label={previewData.label}
        onClose={() => setPreviewData({...previewData, isOpen: false})}
      />
    </>
  )
}

export default DocumentsFGL
